import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Spacing } from '@/shared/theme';
import { BorisScreener } from '@/shared/borisScreenerTheme';
import { Title, Text, CenteredColumn } from '@/shared/ui';
import { RgTextField, RgBody, RgFoot } from '@/shared/borisFields';
import { PasswordHelper } from '@/components/PasswordHelper';
import type { UserData } from '@/types/registration';
import signupImage from '@/assets/images/Signup/signup.svg';
import { StartOverModal } from '@/components/StartOverModal';
import { PrivacyPolicyModal } from '@/components/modals/PrivacyPolicyModal';
import { CareForHeartConsentModal } from '@/components/modals/CareForHeartConsentModal';
import DisclaimersCheckboxes, { Disclaimers } from './DisclaimersCheckboxes';
import CmsConsentCheckboxes, { type CmsConsents, CONSENT_KEYS } from './CmsConsentCheckboxes';
import { CONSENT_ACCESS_CMS_PILOT, CONSENT_CARE_FOR_HEART } from '@/consts/consentTypes';
import { useApiError } from '@/context/ApiErrorContext';
import { apiClient, HelloServerErrorCodes, type Locale, type ConsentMetadata } from '@/services/api';
import { cmsApiClient } from '@/services/cmsApiClient';
import { buildAlignmentConditions } from '@/utils/alignmentConditions';
import { toIsoBirthDate } from '@/utils/date';
import { useAnalytics } from '@/hooks';
import analytics from '@/services/Analytics';
import { useAppFlow, STEP_KEYS } from '@/hooks/useAppFlow';
import {
  VIEW_SIGNUP,
  SUBMIT_SIGNUP,
  REGISTRATION_SUCCESS,
  REGISTRATION_FAILED,
  ALIGNMENT_FAILED,
  ALIGNMENT_COMPLETE,
  ALIGNMENT_TIMEOUT,
  VIEW_ALREADY_REGISTERED,
} from '@/consts/events';
import { useAlignmentPolling, type AlignmentSettlement } from '@/hooks/useAlignmentPolling';
import { useRegistration } from '@/context/RegistrationContext';
import { AxiosError } from 'axios';
import { FormikValues, useFormik } from 'formik';
import * as yup from 'yup';
import {
  useFormValues,
  PASSWORD_KEY,
  RETYPE_PASSWORD_KEY,
} from '@/hooks/useFormValues';

// Boris `.rg-screen`: fills the frame's main column (flex:1/min-height:0
// against Layout's FlowMain) and splits into RgBody (scrolls) + RgFoot (pinned
// flex sibling); see borisFields.tsx / MedicareCoverageStep for why that split
// replaces a plain flowing CTA (without it, the submit button just trails the
// consent list instead of staying pinned like Boris's ConsentScreen `.rg-foot`).
const FormElement = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const TitleStyled = styled(Title)`
  text-align: left;
  width: 100%;
  margin-bottom: ${Spacing.x4}px !important;
`;

// Password + retype now use the Boris `RgTextField` (label-above, white rounded
// r15/h60, ink focus) to match the rest of the flow (ShippingStep is the
// template); the legacy MUI floating-label form has been retired here.
const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
`;

// Boris "consent doc" card (Happy Hearts restyle): groups the CMS + disclaimer
// consents on a single white, rounded, bordered surface (matches the design's
// `.rg-consent-doc`). Purely presentational — consent keys, required gating,
// and copy are unchanged.
const ConsentCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${Spacing.x1}px;
  background: ${BorisScreener.white};
  border: 1px solid ${BorisScreener.line};
  border-radius: 16px;
  padding: ${Spacing.x2}px;
  margin-bottom: ${Spacing.x3}px;
`;

// Boris design-system primary button (.dsb-l): 72px pill, 22px label, disabled
// keeps the base green at opacity .38 (not a gray swap). Matches the shared ui
// Button's tokens; kept local so it can carry the native `disabled`/loading
// state the form relies on.
const SubmitButton = styled.button<{ $disabled: boolean }>`
  align-self: flex-start;
  width: 100%;
  max-width: 340px;
  height: 72px;
  border-radius: 32px;
  border: none;
  color: #fff;
  font-family: inherit;
  font-size: 22px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${BorisScreener.btnPrimary};
  opacity: ${({ $disabled }) => ($disabled ? 0.38 : 1)};
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  transition: background 0.16s ease, opacity 0.16s ease;

  &:hover {
    background: ${({ $disabled }) => ($disabled ? BorisScreener.btnPrimary : BorisScreener.btnPrimaryHover)};
  }
`;

const Spinner = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  display: inline-block;
  animation: rg-spin 0.8s linear infinite;
  @keyframes rg-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

// Alignment-poll processing splash (RND-36355). Mirrors the EligibilityResultStep
// processing layout so the "waiting on CMS" screens read consistently.
const ProcessingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: ${Spacing.x2}px;
  text-align: center;
  padding: ${Spacing.x4}px ${Spacing.x2}px;
`;

// Email + phone are now captured on the identity screen (per PRD 01 6.1 / Boris);
// this step only sets the password (until the account model moves to OTP).
const signUpFormValues = [
  PASSWORD_KEY,
  RETYPE_PASSWORD_KEY,
];

const PasswordAccountForm: React.FC = () => {
  const navigate = useNavigate();
  const { registrationData, updateAccount, setUserData, webregUserId, sampleGroup, utmParams } =
    useRegistration();
  const firstName = registrationData.medicareCoverage.firstName;
  const { showApiError } = useApiError();
  const [isLoading, setIsLoading] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [disclaimers, setDisclaimers] = useState<Disclaimers>({
    enableSmsMarketing: false,
    privacyPracticeReviewed: false,
    consentToShareData: false,
  });
  const [cmsConsents, setCmsConsents] = useState<CmsConsents>({
    voluntary: false,
    apiData: false,
    pcpSharing: false,
    controlGroup: false,
  });
  const [showStartOverModal, setShowStartOverModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const { track, trackView } = useAnalytics();
  const { t, i18n } = useTranslation();
  // Reuse the centralized password/retype Yup schemas + field config (rules,
  // messages) so validation stays identical to the legacy form.
  const formValues = useFormValues(signUpFormValues);

  // RND-36355: alignment result polling. After signup + POST /alignment succeed
  // we hold the account's userData back until the alignment submission reaches a
  // terminal state, showing a processing splash meanwhile. autoNavigateOnComplete
  // keys on userData.authToken, so deferring setUserData keeps the user on this
  // step until the poll settles (or times out), then advances the flow.
  const [isAligning, setIsAligning] = useState(false);
  const [alignmentPoll, setAlignmentPoll] = useState<{ submissionId: string; authToken: string } | null>(null);
  const pendingUserDataRef = useRef<UserData | null>(null);

  const handleAlignmentSettled = useCallback(
    (result: AlignmentSettlement) => {
      const base = pendingUserDataRef.current;
      pendingUserDataRef.current = null;
      setAlignmentPoll(null);
      setIsAligning(false);
      if (!base) return;

      const alignmentOutcome = result.status === 'complete' ? result.outcome : undefined;
      // submissionId lets ops correlate a client-side poll outcome (esp. a timeout)
      // with the backend's own independent CMS poll for the same submission.
      const submissionId = base.alignmentSubmissionId;
      if (result.status === 'complete') {
        // The backend guarantees a recognized outcome on every 200/complete result
        // (alignmentOutcome.ts maps all codes; unrecognized codes fail closed to a
        // 422/error, never complete), so `?? 'unknown'` is a defensive fallback only.
        // resultCode carries the raw CMS code for ops correlation.
        track({
          event: ALIGNMENT_COMPLETE,
          properties: { outcome: alignmentOutcome ?? 'unknown', resultCode: result.resultCode, submissionId },
        });
      } else if (result.status === 'timeout') {
        track({ event: ALIGNMENT_TIMEOUT, properties: { submissionId } });
      } else {
        // reason distinguishes a 404 (missing submission record) from a 422/401/403
        // (CMS reported the alignment errored, or the authToken was rejected) for
        // ops observability. stage separates this poll-path failure from the
        // POST /alignment submit-path failure below, which fires the same event
        // with a different property shape.
        track({ event: ALIGNMENT_FAILED, properties: { stage: 'poll', reason: result.reason, submissionId } });
      }

      // Setting userData completes the step → autoNavigateOnComplete advances to
      // shipping. Alignment failure/timeout is non-blocking: registration already
      // succeeded and the backend continues polling CMS independently.
      setUserData({ ...base, alignmentOutcome });
    },
    [track, setUserData],
  );

  const { phase: alignmentPhase } = useAlignmentPolling({
    submissionId: alignmentPoll?.submissionId ?? null,
    authToken: alignmentPoll?.authToken ?? null,
    onSettled: handleAlignmentSettled,
  });

  // Determine if Care for Heart consent is required based on promo code configuration
  const isCareForHeartConsentRequired =
    registrationData.promoCodeData?.appConfig?.medAdvisors !== 'off';

  // Integrate useAppFlow with back navigation blocking
  const { resetBlocker } = useAppFlow(STEP_KEYS.CREATE_ACCOUNT, {
    onBackNavigationBlocked: () => {
      setShowStartOverModal(true);
    },
    autoNavigateOnComplete: true,
  });

  useEffect(() => {
    trackView({ event: VIEW_SIGNUP });
  }, [trackView]);

  const handleStartOver = () => {
    // Navigate to landing page (which will clear all data on mount)
    setShowStartOverModal(false);
    navigate('/');
  };

  const onSubmit = async (submittedValues: FormikValues) => {
    // The submit button is disabled until every required consent is checked, but
    // formik's submit handler only validates the password — a stray submit (Enter
    // in a field) would otherwise bypass the consent gate. Re-check it here so
    // signup/alignment never fire without the required consents.
    const consentsSatisfied =
      CONSENT_KEYS.every((key) => cmsConsents[key]) &&
      (!isCareForHeartConsentRequired || disclaimers.privacyPracticeReviewed);
    if (!consentsSatisfied) {
      return;
    }

    track({
      event: SUBMIT_SIGNUP,
      properties: {
        is_privacy_checkbox_appearing: true,
        is_privacy_checkbox_checked: disclaimers.privacyPracticeReviewed,
        is_consent_checkbox_appearing: true,
        is_consent_checkbox_checked: disclaimers.consentToShareData,
        is_care_for_heart_consent_checkbox_appearing: isCareForHeartConsentRequired,
        is_care_for_heart_consent_checkbox_checked: disclaimers.privacyPracticeReviewed,
      },
    });

    setIsLoading(true);

    try {
      const { password, retype_password } = submittedValues;
      const { medicareCoverage, account } = registrationData;
      // Email + phone were captured on the identity screen (per PRD 01 6.1 / Boris).
      const { email, phoneNumber: phone } = account;

      // Update account data in context
      updateAccount({
        password,
        retypePassword: retype_password,
      });

      const now = Date.now();
      const consents: ConsentMetadata[] = [
        {
          version: 1,
          status: 'consent',
          dateReported: now,
          source: 'access_registration',
          userMetadataObjectType: CONSENT_ACCESS_CMS_PILOT,
        },
      ];

      if (isCareForHeartConsentRequired && disclaimers.privacyPracticeReviewed) {
        consents.push({
          version: 1,
          status: 'consent' as const,
          dateReported: now,
          source: 'access_registration',
          userMetadataObjectType: CONSENT_CARE_FOR_HEART,
        });
      }

      const response = await apiClient.signup({
        email,
        password,
        phoneNumber: phone,
        personalDetails: {
          firstname: medicareCoverage.firstName,
          lastname: medicareCoverage.lastName,
          ['date_of_birth']: medicareCoverage.dateOfBirth,
          city: '',
          phonenumber: phone,
          state: '',
          streetaddress: '',
          zipcode: medicareCoverage.zipCode,
        },
        enableSmsMarketing: disclaimers.enableSmsMarketing,
        privacyPracticeReviewed: disclaimers.privacyPracticeReviewed,
        consentToShareData: disclaimers.consentToShareData,
        registrationMetadata: {
          webregUserId,
          ...(utmParams ?? {}),
        },
        locale: i18n.language as Locale,
        promoCode: registrationData.promoCodeData!.code,
        sampleGroup,
        consentsMetadata: { consents },
        cmsAccessEnrollment: { tracks: registrationData.selectedTracks },
      });

      let alignmentSubmissionId: string | undefined;
      const primaryTrack = registrationData.selectedTracks[0];
      if (!primaryTrack) {
        console.warn('Alignment skipped: selectedTracks is empty');
      } else {
        try {
          const alignmentResponse = await cmsApiClient.postAlignment(
            {
              track: primaryTrack,
              mbi: medicareCoverage.mbi,
              patientName: {
                family: medicareCoverage.lastName,
                given: [medicareCoverage.firstName],
              },
              gender: medicareCoverage.gender ?? 'unknown',
              birthDate: toIsoBirthDate(medicareCoverage.dateOfBirth) || medicareCoverage.dateOfBirth,
              conditions: buildAlignmentConditions(registrationData, primaryTrack),
              consentVoluntary: cmsConsents.voluntary,
              consentApiDataSharing: cmsConsents.apiData,
              consentPcpSharing: cmsConsents.pcpSharing,
              consentControlGroup: cmsConsents.controlGroup,
              consentTimestamp: now,
            },
            response.authToken,
          );
          alignmentSubmissionId = alignmentResponse.submissionId;
        } catch (err) {
          const errorClass = err instanceof Error ? err.constructor.name : 'UnknownError';
          console.warn('Alignment call failed; registration continues:', errorClass);
          // stage separates this submit-path failure (POST /alignment) from the
          // poll-path failure in handleAlignmentSettled, which fires the same
          // event with a different property shape.
          track({ event: ALIGNMENT_FAILED, properties: { stage: 'submit', error: errorClass } });
        }
      }

      // Identify user for analytics
      analytics.identifyUser(response.user.analyticsID);

      // Get solutions with devices and shipping
      const userSolutions =
        response.promoCode?.solutions?.filter((s) => s.hasDevice && s.hasShipping) || [];

      // Store user data for shipping step
      const userData: UserData = {
        authToken: response.authToken,
        solutions: userSolutions,
        email: response.user.email,
        phonenumber: phone,
        firstName: medicareCoverage.firstName,
        lastName: medicareCoverage.lastName,
        analyticsID: response.user.analyticsID,
        OTPLink: response.otpLink,
        alignmentSubmissionId,
      };

      track({
        event: REGISTRATION_SUCCESS,
        properties: {
          screen: 'access_registration',
          analyticsID: response.user.analyticsID,
        },
      });

      if (alignmentSubmissionId && response.authToken) {
        // Defer setUserData: poll the alignment result first (RND-36355). The
        // built userData is held in a ref and finalized (with alignmentOutcome)
        // by handleAlignmentSettled once the poll reaches a terminal state.
        // Gate on authToken too, matching useAlignmentPolling's own guard — the
        // hook never starts without both, so starting the splash without a
        // token would strand the user with no timeout.
        pendingUserDataRef.current = userData;
        setIsAligning(true);
        setAlignmentPoll({ submissionId: alignmentSubmissionId, authToken: response.authToken });
      } else {
        // No alignment submission (empty track or POST /alignment failed), or no
        // authToken to poll with: proceed
        // immediately, exactly as before.
        setUserData(userData);
      }
    } catch (error) {
      track({
        event: REGISTRATION_FAILED,
        properties: {
          screen: 'access_registration',
          error: error instanceof Error ? error.message : 'Unknown error',
        },
      });

      if (error instanceof AxiosError) {
        const errorNumber = error.response?.data?.number;
        if (errorNumber === HelloServerErrorCodes.UserAlreadyExists || errorNumber === HelloServerErrorCodes.EligibilityRecordAttachedToUser) {
          trackView({ event: VIEW_ALREADY_REGISTERED });
        }
      }

      // Show API error modal
      showApiError();
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      [PASSWORD_KEY]: '',
      [RETYPE_PASSWORD_KEY]: '',
    },
    validationSchema: yup.object({
      [PASSWORD_KEY]: formValues[PASSWORD_KEY]?.validationSchema,
      [RETYPE_PASSWORD_KEY]: formValues[RETYPE_PASSWORD_KEY]?.validationSchema,
    }),
    validateOnMount: true,
    onSubmit,
  });

  const allCmsConsentsChecked = CONSENT_KEYS.every(key => cmsConsents[key]);
  const isSubmitDisabled =
    !formik.isValid ||
    !allCmsConsentsChecked ||
    (isCareForHeartConsentRequired && !disclaimers.privacyPracticeReviewed);

  // Match the legacy field behaviour: surface an error only once the field is
  // touched and has content (don't flash "required" on an untouched empty field).
  const fieldError = (key: keyof typeof formik.values): string | undefined =>
    formik.touched[key] && formik.values[key]
      ? (formik.errors[key] as string | undefined)
      : undefined;

  const revealLabels = { show: t('form.show-password'), hide: t('form.hide-password') };

  // While the alignment result is being polled, swap the form for a processing
  // splash (RND-36355). The modals below stay mounted so the useAppFlow back-nav
  // blocker can still surface the Start Over modal during polling.
  const alignmentSplash = (
    <ProcessingContainer role="status" aria-live="polite">
      <CenteredColumn span={8}>
        <Title size="h2" centered>
          {t('create-account.aligning.title')}
        </Title>
        <Text size="p1" centered>
          {alignmentPhase === 'taking-longer'
            ? t('create-account.aligning.taking-longer')
            : t('create-account.aligning.message')}
        </Text>
      </CenteredColumn>
    </ProcessingContainer>
  );

  return (
    <>
      {isAligning ? (
        alignmentSplash
      ) : (
        <FormElement onSubmit={formik.handleSubmit} noValidate>
        <RgBody>
          <CenteredColumn span={6}>
            <img src={signupImage} alt="" />
            <TitleStyled size="h2">
              <Trans
                i18nKey={firstName ? 'create-account.title-with-name' : 'create-account.title'}
                values={firstName ? { firstName } : {}}
                components={{
                  hide: <span data-private />,
                }}
              />
            </TitleStyled>
          </CenteredColumn>

          <CenteredColumn span={8}>
            <Fields>
              <RgTextField
                name={PASSWORD_KEY}
                label={t('create-account.password')}
                type="password"
                value={formik.values[PASSWORD_KEY]}
                onChange={formik.handleChange}
                onFocus={() => setPasswordFocused(true)}
                onBlur={(e) => {
                  formik.handleBlur(e);
                  setPasswordFocused(false);
                }}
                autoComplete="new-password"
                error={fieldError(PASSWORD_KEY)}
                revealLabels={revealLabels}
              />
              <PasswordHelper
                value={formik.values[PASSWORD_KEY]}
                isFocused={passwordFocused}
                id="password-helper"
              />
              <RgTextField
                name={RETYPE_PASSWORD_KEY}
                label={t('create-account.retype-password')}
                type="password"
                value={formik.values[RETYPE_PASSWORD_KEY]}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                autoComplete="new-password"
                error={fieldError(RETYPE_PASSWORD_KEY)}
                revealLabels={revealLabels}
              />
            </Fields>

            <ConsentCard>
              <CmsConsentCheckboxes
                consents={cmsConsents}
                onChange={(key, value) => setCmsConsents(prev => ({ ...prev, [key]: value }))}
              />
              <DisclaimersCheckboxes
                disclaimers={disclaimers}
                setDisclaimers={setDisclaimers}
                onPrivacyClick={() => setShowPrivacyModal(true)}
                onInformedConsentClick={() => setShowConsentModal(true)}
                isCareForHeartConsentRequired={isCareForHeartConsentRequired}
              />
            </ConsentCard>
          </CenteredColumn>
        </RgBody>

        <RgFoot>
          <SubmitButton
            type="submit"
            data-testid="submit-btn"
            data-loading={isLoading}
            $disabled={isSubmitDisabled || isLoading}
            disabled={isSubmitDisabled || isLoading}
            aria-label={t('create-account.submit')}
          >
            {isLoading ? <Spinner aria-hidden="true" /> : t('create-account.submit')}
          </SubmitButton>
        </RgFoot>
        </FormElement>
      )}

      {/* Start Over Modal */}
      <StartOverModal
        isOpen={showStartOverModal}
        onConfirm={handleStartOver}
        onCancel={() => {
          setShowStartOverModal(false);
          resetBlocker();
        }}
      />

      {/* Privacy Policy Modal */}
      <PrivacyPolicyModal open={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />

      {/* Care for Heart Consent Modal */}
      <CareForHeartConsentModal
        open={showConsentModal}
        onClose={() => setShowConsentModal(false)}
      />
    </>
  );
};

export { PasswordAccountForm };
